import {
  type Active,
  DragOverlay,
  type Over,
  useDndContext,
} from "@dnd-kit/core";
import { cn } from "@repo/shared/utils";

export type AgendaDataUpdater = (
  updater: (itemIds: string[]) => string[]
) => void;

export interface DragData {
  id: string;
  ind: number;
  type: "agendaModule" | "agendaItem" | "document";
  container: string;
  updateOrder: AgendaDataUpdater;
  index: number;
  overlay: React.ReactNode;
}

export const EditorDragOverlay = () => {
  const { active } = useDndContext();

  const activeData = active?.data.current as DragData | undefined;

  return (
    <DragOverlay className="pointer-events-none">
      {activeData?.overlay}
    </DragOverlay>
  );
};

export type DragInsertPos = "before" | "after" | null;

export const matchingDragType = (
  active: Active | null,
  over: DragData["type"] | Over | null
) => {
  const activeType = (active?.data.current as DragData | undefined)?.type;
  const overType =
    typeof over === "string"
      ? over
      : (over?.data.current as DragData | undefined)?.type;

  return activeType && overType && activeType === overType;
};

export const dragInsertPos = (
  active: Active | null,
  over: Over | null
): DragInsertPos => {
  if (!matchingDragType(active, over)) return null;

  const activeRect = active?.rect.current.translated;
  const overRect = over?.rect;

  if (!activeRect || !overRect) return null; // drag must be active

  return activeRect.top + 24 > overRect.top + overRect.height / 2
    ? "after"
    : "before";
};

export const inDragContainer = (active: Active | null, containerId: string) => {
  const activeData = active?.data.current as DragData | undefined;
  if (!activeData) return false;
  return activeData.container === containerId;
};

export const isDataForSelf = (
  drag: Active | Over | null,
  data: DragData
): boolean => {
  const dragData = drag?.data.current as DragData | undefined;
  if (!dragData) return false;
  return (
    dragData.type === data.type &&
    dragData.container === data.container &&
    dragData.id === data.id &&
    dragData.ind === data.ind
  );
};

export const insertClasses = (insert: DragInsertPos): string =>
  insert !== "before" && insert !== "after"
    ? ""
    : cn(
        /*@tw*/ "before:absolute before:z-20 before:w-full before:h-1 before:rounded-md before:bg-slate-500 before:opacity-50",
        insert === "before"
          ? /*@tw*/ "before:-top-0.5"
          : /*@tw*/ "before:-bottom-0.5"
      );
